import ArrowLeft from "@assets/icons/arrow-left.svg";
import ArrowRight from "@assets/icons/arrow-right.svg";
import CrossIcon from "@assets/icons/cross.svg";
import InfoIcon from "@assets/icons/info.svg";
import { FeatureToggles } from "@core/feature-toggling/feature-toggles";
import { KeyboardKeys, useKeyboardKeyPressed } from "@core/hooks/use-keyboard-key-pressed";
import { useI18n } from "@core/i18n/i18n";
import { AnalyticsCategories } from "@domain/analytics/analytics-types";
import { Exercise, exerciseBodyParts, ExerciseComment } from "@domain/exercise/exercise";
import {
	sendAnalyticsEventExerciseDetailsComment,
	sendAnalyticsEventExerciseDetailsViewVideoOrKeyframe,
	sendAnalyticsEventNavigationClick,
	sendAnalyticsEventPlayVoiceOver,
} from "@domain/library/exercise-analytics";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
import { e2eTestId } from "@e2e/test-ids";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AudioPlayer } from "@ui/components/common/audio-player";
import { Button } from "@ui/components/buttons/button";
import { ButtonIcon } from "@ui/components/buttons/button-icon";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ImageWithLoading } from "@ui/components/common/image-with-loading";
import { Tooltip } from "@ui/components/common/tooltip";
import { TrackingContextProps } from "@ui/components/common/tracking-context-provider";
import { VideoPlayer } from "@ui/components/common/video-player";
import { ExerciseCommentTab } from "@ui/pages/program-builder/dialog/exercise-comment-tab";
import { ExerciseDescriptionTab } from "@ui/pages/program-builder/dialog/exercise-description-tab";
import { BodyPartIcon } from "@ui/components/exercise/body-part-icon";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { getScrollbarStyle } from "@ui/style/scrollbar";
import { isNotNil } from "@utils/assert";
import _ from "lodash";

import { DialogProps } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useMemo, useState } from "react";

const BODY_PART_SEPARATOR = "-";

export enum ExerciseDetailsDialogTabs {
	DESCRIPTION = 0,
	COMMENT = 1,
}

export interface ExerciseDetailsDialogProps extends DialogProps {
	tabHeaderDisabled?: boolean;
	relatedExercises: Exercise[];
	exerciseIndex: number;
	editable?: boolean;
	editorName: string;
	selectedTab?: ExerciseDetailsDialogTabs;
	trackingContext: TrackingContextProps;
}

export const ExerciseDetailsDialog: React.FC<ExerciseDetailsDialogProps> = props => {
	const {
		trackingContext,
		tabHeaderDisabled = false,
		relatedExercises,
		exerciseIndex,
		editable = true,
		editorName,
		selectedTab: tabSelected = ExerciseDetailsDialogTabs.DESCRIPTION,
		onHide,
		...otherProps
	} = props;

	const [activeIndex, setActiveIndex] = useState(tabSelected);

	const [currentExerciseIndex, setCurrentExerciseIndex] = useState(exerciseIndex);
	const currentExercise = useMemo(
		() => relatedExercises[currentExerciseIndex],
		[currentExerciseIndex, relatedExercises],
	);

	const [initialComment, setInitialComment] = useState<ExerciseComment | undefined>(currentExercise.comment);
	const [arrowClickCount, setArrowClickCount] = useState(0);

	const [isKeyframeDisplayed, setIsKeyframeDisplayed] = useState(!_.isEmpty(currentExercise.keyframeUrls));

	const { format } = useI18n();
	const { formatExerciseBodyPart, formatExerciseCategory } = useExerciseFormatter();
	const videoRef = React.useRef<HTMLVideoElement>(null);

	const displayCommentTab = useMemo(
		() => !tabHeaderDisabled && (editable || isNotNil(currentExercise.comment)) && FeatureToggles.ENABLE_COMMENTS,
		[currentExercise.comment, tabHeaderDisabled, editable],
	);

	const changeExercise = useCallback(
		(index: number) => {
			let newIndex = index;
			if (index < 0) {
				newIndex = 0;
			} else if (index >= relatedExercises.length) {
				newIndex = relatedExercises.length - 1;
			}

			sendAnalyticsEventExerciseDetailsComment(
				trackingContext.programId,
				AnalyticsCategories[trackingContext.category],
				currentExercise,
				initialComment,
			);
			setArrowClickCount(arrowClickCount + 1);

			setCurrentExerciseIndex(newIndex);
			setIsKeyframeDisplayed(!_.isEmpty(relatedExercises[newIndex].keyframeUrls));
			setInitialComment(relatedExercises[newIndex].comment);
		},
		[
			relatedExercises,
			trackingContext.programId,
			trackingContext.category,
			currentExercise,
			initialComment,
			arrowClickCount,
		],
	);

	const onHideDialog = useCallback(() => {
		sendAnalyticsEventExerciseDetailsComment(
			trackingContext.programId,
			AnalyticsCategories[trackingContext.category],
			currentExercise,
			initialComment,
		);
		sendAnalyticsEventNavigationClick(arrowClickCount);
		onHide();
	}, [arrowClickCount, currentExercise, initialComment, onHide, trackingContext.category, trackingContext.programId]);

	useKeyboardKeyPressed({
		[KeyboardKeys.ArrowLeft]: () => changeExercise(currentExerciseIndex - 1),
		[KeyboardKeys.ArrowRight]: () => changeExercise(currentExerciseIndex + 1),
	});

	return (
		<StyledBaseDialog
			draggable={false}
			resizable={false}
			closable={false}
			blockScroll
			header={
				<DialogHeader>
					<Navigation>
						<ButtonIcon
							icon={ArrowLeft}
							onClick={() => changeExercise(currentExerciseIndex - 1)}
							size="condensed"
							disabled={currentExerciseIndex <= 0}
						/>
						<p>{`${currentExerciseIndex + 1} ${format("exerciseDetailsModal.navigation.outOf")} ${relatedExercises.length}`}</p>
						<ButtonIcon
							icon={ArrowRight}
							onClick={() => changeExercise(currentExerciseIndex + 1)}
							size="condensed"
							disabled={currentExerciseIndex >= relatedExercises.length - 1}
						/>
					</Navigation>
					<StyledCrossIcon
						width={40}
						height={40}
						color={colors.black}
						data-test={e2eTestId.exerciseDetailsDialog.closeButton}
						onClick={onHideDialog}
					/>
				</DialogHeader>
			}
			onHide={() => {}}
			width={DIALOG_SIZE.M}
			{...otherProps}
		>
			<ExerciseHeader>
				<MainInfo>
					<BodyParts className="p-size-s">
						{exerciseBodyParts(currentExercise).map(bodyPart => (
							<BodyPartPill key={bodyPart}>
								<BodyPartIcon bodyPart={bodyPart} />
								{formatExerciseBodyPart(bodyPart)}
								<p>{BODY_PART_SEPARATOR}</p>
							</BodyPartPill>
						))}
						<p>{formatExerciseCategory(currentExercise.category)}</p>
					</BodyParts>
					<p className="p-size-l-bold">{currentExercise.title}</p>
				</MainInfo>
				<div>
					{!_.isEmpty(currentExercise.keyframeUrls) && (
						<Button
							style={{ whiteSpace: "nowrap" }}
							uiStyle="secondary_B"
							label={format(
								isKeyframeDisplayed
									? "exerciseDetailsModal.displayType.video"
									: "exerciseDetailsModal.displayType.keyframes",
							)}
							onClick={() => {
								sendAnalyticsEventExerciseDetailsViewVideoOrKeyframe(currentExercise.id, isKeyframeDisplayed);
								setIsKeyframeDisplayed(!isKeyframeDisplayed);
							}}
						/>
					)}
				</div>
			</ExerciseHeader>
			<AssetsContainer>
				{isKeyframeDisplayed ? (
					<KeyframeContainer>
						{currentExercise.keyframeUrls.map((keyframe, index) => (
							<ImageContainer key={`${keyframe}-${index}`}>
								<ImageWithLoading src={keyframe} />
							</ImageContainer>
						))}
					</KeyframeContainer>
				) : (
					<>
						<VideoContainer>
							<VideoPlayer
								key={currentExercise.id}
								ref={videoRef}
								url={currentExercise.videoUrl}
								loop
								autoPlay
								playsInline
								displayVideoGradient={false}
								poster={currentExercise.thumbnails.largeThumbnailUrl}
							/>
						</VideoContainer>
						<AudioPlayer
							key={currentExercise.id}
							url={currentExercise.audioUrl}
							controls
							onPlay={() => sendAnalyticsEventPlayVoiceOver(currentExercise)}
						/>
					</>
				)}
			</AssetsContainer>
			<StyledTabView
				tabHeaderDisabled={tabHeaderDisabled}
				activeIndex={activeIndex}
				onTabChange={e => setActiveIndex(e.index)}
			>
				<TabPanel header={<p className="p-size-s-bold">{format("exerciseDetailsModal.description.title")}</p>}>
					<ExerciseDescriptionTab exercise={currentExercise} />
				</TabPanel>
				{displayCommentTab && (
					<TabPanel
						header={
							<HeaderTab>
								<div className="p-size-s-bold">
									{editable
										? format("exerciseDetailsModal.comment.title")
										: format("exerciseDetailsModal.comment.title.readonly")}
								</div>
								{editable && (
									<Tooltip content={format("exerciseDetailsModal.comment.help")} placement="auto">
										<IconContainer>
											<InfoIcon width={20} height={20} color={colors.grey} />
										</IconContainer>
									</Tooltip>
								)}
							</HeaderTab>
						}
					>
						<ExerciseCommentTab
							key={currentExercise.draggableId}
							exercise={currentExercise}
							editable={editable}
							editorName={editorName}
						/>
					</TabPanel>
				)}
			</StyledTabView>
		</StyledBaseDialog>
	);
};

const StyledBaseDialog = styled(BaseDialog)`
	.p-dialog-content {
		padding: 24px;
	}

	.p-dialog-header {
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
		overflow: hidden;
		padding: 16px;
		border-bottom: 1px solid ${colors.primary25};

		.p-dialog-header-icons {
			display: none;
		}
	}

	.p-dialog-footer {
		border-bottom-left-radius: 8px;
		border-bottom-right-radius: 8px;
	}

	@media ${physioMeMobileBreakpoint} {
		.p-dialog-header,
		.p-dialog-footer {
			border-radius: 0;
		}
	}
`;

const DialogHeader = styled.div`
	${row(0, "spacebetween", "center")};
`;

const Navigation = styled.div`
	${row(8, "center", "center")};
	font-weight: normal;
	font-size: 12px;
	color: ${colors.grey};
`;

const StyledCrossIcon = styled(CrossIcon)`
	cursor: pointer;
	padding: 8px;
	border-radius: 50%;

	:hover {
		background-color: ${colors.primaryBackground};
	}
`;

const ExerciseHeader = styled.div`
	${row(10, "spacebetween", "center")};
	padding-bottom: 24px;
	@media ${physioMeMobileBreakpoint} {
		${stack(16)};
		padding-bottom: 16px;
	}
`;

const MainInfo = styled.div`
	${stack(4)};
`;

const BodyParts = styled.div`
	display: flex;
	flex-wrap: wrap;
	color: ${colors.grey80};
	column-gap: 8px;
`;

const BodyPartPill = styled.div`
	display: flex;
	place-items: center;
	gap: 8px;
`;

const AssetsContainer = styled.div`
	margin-bottom: 24px;
`;

const KeyframeContainer = styled.div`
	${row(16)};
	margin-right: -24px;
	padding-bottom: 8px;
	padding-right: 8px;
	overflow: auto;
	${getScrollbarStyle(8)};

	@media ${physioMeMobileBreakpoint} {
		margin-right: -16px;
	}
`;

const ImageContainer = styled.div`
	width: 200px;
	height: 200px;
	border: 2px solid ${colors.primary10};
	border-radius: 16px;
	overflow: hidden;
	flex: 0 0 auto;
	@media ${physioMeMobileBreakpoint} {
		width: 150px;
		height: 150px;
	}
`;

const VideoContainer = styled.div`
	border: 2px solid ${colors.primary10};
	overflow: hidden;
	border-radius: 16px;
	margin-bottom: 16px;
`;

const StyledTabView = styled(TabView)<{ tabHeaderDisabled: boolean }>`
	${({ tabHeaderDisabled }) =>
		tabHeaderDisabled &&
		css`
			.p-tabview-nav {
				display: none;
			}

			.p-tabview-panels {
				padding: 0;
			}
		`}
`;

const IconContainer = styled.div`
	height: 16px;
`;
const HeaderTab = styled.div`
	${row(8, "flexstart", "center")}
`;
